/**
 * Calibration remediation — the /admin?tab=calibration-fixes surface.
 *
 * Finds team members with a completed, rated check-in who are in NO calibration
 * session, and (from Phase 2) fixes them. Separate from calibrationService
 * because every route here is admin-gated and the two have different audiences:
 * calibrationService is what a People Leader's own screens call.
 *
 * Routes via /performance/... (gateway prefix) → /api/v1/admin/calibration/... on
 * performance-service.
 */

import { API_CONFIG } from '@/config/api'
import { authenticatedFetch } from '../authenticatedFetch'

// ============================================================================
// TYPES
// ============================================================================

/** Why the row can't be actioned. Mirrors the server's blocker codes. */
export type StrandedBlockerCode =
  | 'no_justification'
  | 'no_session'
  | 'ambiguous_session'
  /** Moved since their check-in, which a stand-in did: their group can't be told. */
  | 'moved_unclear'

/** How the member came to be stranded — drives the grouping in the UI. */
export type StrandedCause =
  | 'late_check_in'
  | 'partial_submission'
  | 'never_submitted'

/** How the server worked out which session the People Leader belongs to. */
export type SessionResolutionRule =
  | 'submissions'
  | 'roster'
  | 'one-up'
  | 'ambiguous'
  | 'none'

export interface StrandedBlocker {
  code: StrandedBlockerCode
  message: string
}

export interface ResolvedSession {
  session_id: string | null
  status: string | null
  rule: SessionResolutionRule
  /** Who chairs it — the People Leader presents to this person. */
  facilitator_id?: string | null
  facilitator_name?: string | null
  /** Only present when rule === 'ambiguous'. */
  candidates?: string[]
}

export interface StrandedMember {
  /** The People Leader's own One-Up — who they present to, or would. */
  expected_chair_id?: string | null
  expected_chair_name?: string | null
  employee_id: string
  employee_name: string | null
  job_title: string | null
  /** The People Leader who did their check-in - who presents them this cycle. */
  manager_id: string
  manager_name: string
  /** They report to someone else today (`now_reports_to`) than who did their check-in. */
  moved_since_checkin?: boolean
  now_reports_to?: { employee_id: string; name: string }[]
  review_session_id: string
  /** Lowercase-hyphen form, e.g. 'full' | 'not-yet-full' | 'exceptional'. */
  rating: string
  completed_date: string | null
  justification_chars: number
  kpa_count: number
  cause: StrandedCause
  resolved_session: ResolvedSession
  blockers: StrandedBlocker[]
  /** Only ever true when includeAwaiting was requested. */
  awaiting_submitter: boolean
  remediable: boolean
  available_actions: ('confirm' | 'publish')[]
  /** Plain-English description of what the action would do. Shown verbatim. */
  what_happens: string
}

export interface StrandedSummary {
  /** Genuinely stranded — excludes the awaiting rows even when they're shown. */
  stranded: number
  /** How many rows came back, stranded + awaiting. */
  shown?: number
  blocked: number
  people_leaders: number
  /**
   * Uncovered, but their People Leader is still submitting into a session that
   * is still collecting — normal mid-cycle state, NOT stranded. Kept out of
   * `rows` unless includeAwaiting, and reported here so a bounded list never
   * reads as "that's everyone".
   */
  awaiting_submitter: number
  awaiting_people_leaders: number
  by_cause?: Record<string, number>
  by_session_status?: Record<string, number>
}

export interface StrandedResponse {
  review_type: string
  review_year: number
  /**
   * The employee directory was unreachable, so `rows` is INCOMPLETE. Never
   * render this as "all clear" — the API returns [] on failure too.
   */
  pairs_unavailable: boolean
  rows: StrandedMember[]
  summary: StrandedSummary
  message?: string
}

export interface StrandedQuery {
  reviewType?: 'mid-year' | 'year-end'
  reviewYear: number
  managerId?: string
  sessionId?: string
  includeBlocked?: boolean
  /** Also list the members whose People Leader simply hasn't finished yet. */
  includeAwaiting?: boolean
}

/** Error carrying the server's own refusal text. */
export interface RemediationError extends Error {
  status: number
  detail: string
}

/** What the stranded worklist can apply. */
export type WorklistAction = 'confirm' | 'publish'
/** Everything the ledger records, including HR rating corrections. */
export type RemediationAction = WorklistAction | 'rating'

export interface RemediationResult {
  employee_id: string
  employee_name: string | null
  manager_id?: string
  manager_name?: string | null
  review_session_id?: string
  rating?: string
  /** Set when the operator moved them off their check-in value. */
  agreed_rating?: string | null
  kpa_count?: number
  kpa_published?: number
  /** False = refused for this member; the rest of the batch still ran. */
  applied: boolean
  reason: string | null
}

export interface RemediateResponse {
  session_id: string
  action: RemediationAction
  dry_run: boolean
  total: number
  applied: number
  refused: number
  results: RemediationResult[]
}

export interface LedgerEntry {
  id: string
  session_id: string
  employee_id: string
  employee_name: string | null
  manager_id: string
  manager_name: string | null
  review_session_id: string | null
  action: RemediationAction
  reason: string
  /** Present only on 'rating' rows — the two values that changed. */
  rating_lane?: 'initial' | 'calibrated'
  rating_before?: string | null
  rating_after?: string | null
  applied_by: string
  applied_at: string | null
  undone_at: string | null
  undone_by: string | null
  undo_reason: string | null
  is_undone: boolean
}

export interface LedgerResponse {
  total: number
  limit: number
  offset: number
  rows: LedgerEntry[]
}

// ============================================================================
// SERVICE
// ============================================================================

class CalibrationRemediationService {
  private baseURL = API_CONFIG.baseURL

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...(options?.headers as Record<string, string>) },
    })
    if (!response.ok) {
      // Surface the server's `detail`, not "HTTP error! status: 409". Every
      // refusal this API returns is written for the operator to act on — a
      // status code alone throws that away.
      const raw = await response.text()
      let detail = raw
      try {
        const parsed = JSON.parse(raw)
        detail = typeof parsed?.detail === 'string'
          ? parsed.detail
          : Array.isArray(parsed?.detail)
            // FastAPI validation errors come back as a list.
            ? parsed.detail.map((d: { msg?: string }) => d?.msg).filter(Boolean).join('; ') || raw
            : raw
      } catch {
        /* not JSON — keep the raw body */
      }
      const err = new Error(detail) as RemediationError
      err.status = response.status
      err.detail = detail
      throw err
    }
    return response.status === 204 ? (null as T) : response.json()
  }

  /** The worklist: who is rated but missing from calibration, and why. */
  async listStranded(q: StrandedQuery): Promise<StrandedResponse> {
    const params = new URLSearchParams({
      review_year: String(q.reviewYear),
      review_type: q.reviewType ?? 'mid-year',
    })
    if (q.managerId) params.set('manager_id', q.managerId)
    if (q.sessionId) params.set('session_id', q.sessionId)
    if (q.includeBlocked === false) params.set('include_blocked', 'false')
    if (q.includeAwaiting) params.set('include_awaiting', 'true')
    return this.request<StrandedResponse>(`/performance/admin/calibration/stranded?${params}`)
  }

  /**
   * Apply (or preview) a fix for members of ONE session.
   *
   * The action is not the operator's choice — the worklist row carries
   * `available_actions`, derived from the session's status. `confirm` adds them
   * to a session still running; `publish` writes the rating onto the check-in of
   * a session that has closed.
   *
   * Only employee_ids + reason are sent. Everything else is re-derived
   * server-side at commit, so a stale worklist can't drive a write.
   */
  async remediate(
    sessionId: string,
    action: RemediationAction,
    args: {
      employeeIds: string[]
      reason: string
      reviewType?: 'mid-year' | 'year-end'
      reviewYear: number
      /** employee_id -> agreed rating, where calibration moved someone. */
      ratings?: Record<string, string>
    },
    dryRun: boolean,
  ): Promise<RemediateResponse> {
    const params = new URLSearchParams({
      review_year: String(args.reviewYear),
      review_type: args.reviewType ?? 'mid-year',
      dry_run: String(dryRun),
    })
    return this.request<RemediateResponse>(
      `/performance/admin/calibration/sessions/${sessionId}/remediate/${action}?${params}`,
      {
        method: 'POST',
        body: JSON.stringify({
          employee_ids: args.employeeIds,
          reason: args.reason,
          ...(args.ratings && Object.keys(args.ratings).length ? { ratings: args.ratings } : {}),
        }),
      },
    )
  }

  /**
   * Record a calibration that happened outside the platform, for a People
   * Leader who has no session at all. Creates the session (chaired by their
   * One-Up), submits each member with the agreed rating, and publishes.
   */
  async recordOffline(
    args: {
      managerId: string
      reviewType?: 'mid-year' | 'year-end'
      reviewYear: number
      members: { employee_id: string; rating?: string }[]
      reason: string
    },
    dryRun: boolean,
  ): Promise<RemediateResponse & {
    created_session_id: string | null
    chaired_by_name: string
    manager_name: string
  }> {
    return this.request(
      `/performance/admin/calibration/record-offline?dry_run=${dryRun}`,
      {
        method: 'POST',
        body: JSON.stringify({
          manager_id: args.managerId,
          review_type: args.reviewType ?? 'mid-year',
          review_year: args.reviewYear,
          members: args.members,
          reason: args.reason,
        }),
      },
    )
  }

  /** Everything applied through this tool, newest first. */
  async listRemediations(q: {
    sessionId?: string; employeeId?: string; action?: RemediationAction
    undone?: boolean; limit?: number; offset?: number
  } = {}): Promise<LedgerResponse> {
    const params = new URLSearchParams()
    if (q.sessionId) params.set('session_id', q.sessionId)
    if (q.employeeId) params.set('employee_id', q.employeeId)
    if (q.action) params.set('action', q.action)
    if (q.undone !== undefined) params.set('undone', String(q.undone))
    if (q.limit !== undefined) params.set('limit', String(q.limit))
    if (q.offset !== undefined) params.set('offset', String(q.offset))
    return this.request<LedgerResponse>(`/performance/admin/calibration/remediations?${params}`)
  }

  /** Reverse one applied fix. Refuses (409) if anything changed since. */
  async undoRemediation(remediationId: string, reason: string): Promise<{ note: string }> {
    return this.request(`/performance/admin/calibration/remediations/${remediationId}/undo`, {
      method: 'POST', body: JSON.stringify({ reason }),
    })
  }
}

export const calibrationRemediationService = new CalibrationRemediationService()
export default calibrationRemediationService
